import { IFieldEditor } from '@/shared/types/fieldEditor';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { AIFieldType } from '@trace-8/trace8-schema';
import * as docx from 'docx';
import { DefaultTheme } from 'styled-components/dist/types';
import { formatAiFieldValueForTemplate, formatFieldValueForTemplate } from '../../../utils';
import { convertMarkdownToDocx, getAiSuggestedSectionDOCX } from '../../utils';

const formatText = (text: string): docx.Paragraph => {
  const formattedText = text?.split('\n')?.flatMap((line, index, array) => {
    return index < array.length - 1
      ? [
          new docx.TextRun({
            text: line,
            color: '474747'
          }),
          new docx.TextRun({ break: 1 })
        ]
      : new docx.TextRun({
          text: line,
          color: '474747'
        });
  });

  return new docx.Paragraph({
    children: formattedText,
    spacing: {
      after: 300
    }
  });
};

const createTextRun = (field: IFieldEditor, includeAiSuggested: boolean) => {
  const aiIncluded = field.value === null && field.commentary === null && field.AI?.answers?.length && includeAiSuggested;
  const text = aiIncluded ? `* ${field.name}:` : `${field.name}:`;

  return new docx.TextRun({
    text,
    bold: true,
    size: 24,
    color: '474747'
  });
};

const createTableCell = async (
  field: IFieldEditor,
  includeAiSuggested: boolean,
  fill: string,
  theme: DefaultTheme,
  headerFontColor: string,
  accentColor: string
) => {
  const formattedText =
    field.value === null && field.commentary === null && field.AI?.answers?.length && includeAiSuggested
      ? formatAiFieldValueForTemplate(field.documentFieldType, field.AI.answers[0])
      : formatFieldValueForTemplate(field.documentFieldType, field.value, field.commentary) || 'Not specified';

  let paragraphs = [];

  if (field.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
    if (field.value === null && field.commentary === null && field.AI?.answers?.length && includeAiSuggested) {
      const markdown = await convertMarkdownToDocx({
        markdown: field.AI.answers[0],
        attachments: field.attachments,
        theme,
        headerFontColor,
        accentColor,
        maxImageWidth: 250
      });
      paragraphs.push(...markdown);
    } else {
      if (field.value || field.attachments.length) {
        const markdown = await convertMarkdownToDocx({
          markdown: field.value,
          attachments: field.attachments,
          theme,
          headerFontColor,
          accentColor,
          maxImageWidth: 250
        });
        paragraphs.push(...markdown);
      } else {
        paragraphs.push(formatText('Not specified'));
      }
    }
  } else {
    paragraphs.push(formatText(formattedText || ''));
  }

  return new docx.TableCell({
    children: [
      new docx.Paragraph({
        children: [createTextRun(field, includeAiSuggested)],
        spacing: { after: 300, before: 300 }
      }),
      ...paragraphs
    ],
    margins: {
      left: 300,
      right: 300,
      top: 300,
      bottom: 300
    },
    borders: {
      top: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
      bottom: { style: docx.BorderStyle.SINGLE, size: 40, color: 'FFFFFF' },
      left: { style: docx.BorderStyle.SINGLE, size: 0, color: 'FFFFFF' },
      right: { style: docx.BorderStyle.SINGLE, size: 40, color: 'FFFFFF' }
    },
    width: { size: 6000, type: docx.WidthType.DXA },
    shading: { fill, color: 'FFFFFF' }
  });
};

export const getTwoColumnSection = async (
  fields: IFieldEditor[],
  includeAiSuggested: boolean,
  theme: DefaultTheme,
  headerFontColor: string,
  accentColor: string
) => {
  const rows = [];

  for (let i = 0; i < fields.length; i += 2) {
    const cell1 = await createTableCell(fields[i], includeAiSuggested, 'F4F5F6', theme, headerFontColor, accentColor);
    const cell2 = await createTableCell(fields[i + 1], includeAiSuggested, 'EBF0F4', theme, headerFontColor, accentColor);
    const row = new docx.TableRow({
      children: [cell1, cell2]
    });

    rows.push(row);
  }

  const isAiSuggestedIncluded = (fields: IFieldEditor[], includeAiSuggested: boolean) => {
    return fields.some((field) => (!field.value || field.value === field.AI?.answers[0]) && field.AI?.answers.length) && includeAiSuggested;
  };

  if (isAiSuggestedIncluded(fields, includeAiSuggested)) {
    const aiSuggestedSection = await getAiSuggestedSectionDOCX(fields, theme);
    return [new docx.Table({ rows }), ...aiSuggestedSection];
  }

  return [new docx.Table({ rows })];
};
